import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { nok } from "@/lib/format";
import { TONE_DOT, toneOf, label } from "@/lib/labels";
import { compactKr } from "./money";

/** Right-aligned money cell. Negative in rose, zero muted. */
export const Money = ({ v, compact, className, muted, sign }) => {
  if (v === null || v === undefined) return <span className="text-muted-foreground">–</span>;
  const n = Number(v);
  return (
    <span className={cn("tabular", n < 0 && "text-rose-600 dark:text-rose-400", (muted || n === 0) && "text-muted-foreground", className)}>
      {sign && n > 0 ? "+" : ""}
      {compact ? compactKr(n) : nok(n)}
    </span>
  );
};

/** «Prognose» / «Faktisk» / «Kalkyle» – tiny provenance chip so the two never get confused. */
export const Provenance = ({ kind = "faktisk", className }) => {
  const map = { faktisk: ["Faktisk", "bg-emerald-500"], prognose: ["Prognose", "bg-primary"], kalkyle: ["Kalkyle", "bg-amber-500"], pipeline: ["Vektet pipeline", "bg-slate-400"] };
  const [t, dot] = map[kind] || map.faktisk;
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground", className)} data-testid={`provenance-${kind}`}>
      <span className={cn("h-1.5 w-1.5 rounded-full", dot)} aria-hidden /> {t}
    </span>
  );
};

/** Borderless section with a quiet title row (same family as the dashboard «lenses»). */
export const Panel = ({ title, meta, action, children, className, testId, divided }) => (
  <section className={cn("min-w-0", divided && "lg:border-l lg:border-border/70 lg:pl-8", className)} data-testid={testId}>
    {(title || action) && (
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[13.5px] font-semibold leading-5 tracking-tight">{title}</h2>
          {meta && <p className="mt-0.5 text-[12px] leading-4 text-muted-foreground">{meta}</p>}
        </div>
        {action}
      </header>
    )}
    <div className={cn(title && "mt-4")}>{children}</div>
  </section>
);

export const QuietLink = ({ to, onClick, children, testId }) => {
  const cls = "inline-flex shrink-0 items-center gap-1 text-[12px] text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:text-foreground focus-visible:outline-none";
  if (to)
    return (
      <Link to={to} className={cls} data-testid={testId}>
        {children} <ArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    );
  return (
    <button type="button" onClick={onClick} className={cls} data-testid={testId}>
      {children} <ArrowUpRight className="h-3.5 w-3.5" />
    </button>
  );
};

/** Thin horizontal bar: value/max with an optional reference tick (budget). */
export const Bar = ({ value, max, tone = "primary", tick, className, height = "h-1.5" }) => {
  const w = max ? Math.min(100, Math.max(0, (Number(value) / Number(max)) * 100)) : 0;
  const tickPos = tick != null && max ? Math.min(100, (Number(tick) / Number(max)) * 100) : null;
  const fill = { primary: "bg-primary", ink: "bg-foreground/70", success: "bg-emerald-500", warning: "bg-amber-500", danger: "bg-rose-500", muted: "bg-foreground/30" }[tone] || "bg-primary";
  return (
    <div className={cn("relative w-full overflow-hidden rounded-full bg-muted", height, className)} aria-hidden>
      <div className={cn("h-full rounded-full transition-[width] duration-300", fill)} style={{ width: `${w}%` }} />
      {tickPos != null && <div className="absolute inset-y-0 w-px bg-foreground/60" style={{ left: `${tickPos}%` }} />}
    </div>
  );
};

/** Stacked horizontal bar from segments [{id,value,tone}] – aging buckets, cost groups. */
export const StackBar = ({ segments, total, height = "h-2", className }) => {
  const t = total || segments.reduce((a, s) => a + (Number(s.value) || 0), 0);
  const cls = { neutral: "bg-foreground/25", info: "bg-sky-400", success: "bg-emerald-500", warning: "bg-amber-400", danger: "bg-rose-500", primary: "bg-primary", ink: "bg-foreground/70", muted: "bg-foreground/15" };
  return (
    <div className={cn("flex w-full overflow-hidden rounded-full bg-muted", height, className)} aria-hidden>
      {segments.map((s) => {
        const w = t ? (Number(s.value) / t) * 100 : 0;
        return w > 0 ? <div key={s.id} className={cn("h-full", cls[s.tone] || cls.neutral)} style={{ width: `${w}%` }} title={s.label} /> : null;
      })}
    </div>
  );
};

export const Dot = ({ tone, className }) => <span className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full", TONE_DOT[tone] || TONE_DOT.neutral, className)} aria-hidden />;

export const HealthBadge = ({ health }) => (
  <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12.5px]" data-testid={`health-${health}`}>
    <Dot tone={toneOf(health)} /> {label(health)}
  </span>
);

/** Deterministic finance brief – same visual as the dashboard AI brief. */
export const FinanceBrief = ({ sentences = [], className, meta = "Økonomibrief fra det interne økonomiregisteret · oppdatert nå" }) => {
  if (!sentences.length) return null;
  return (
    <div className={cn("relative pl-7", className)} data-testid="finance-brief">
      <Sparkles className="absolute left-0 top-[5px] h-4 w-4 text-primary" strokeWidth={1.75} aria-hidden />
      <p className="text-[15.5px] leading-7 text-foreground/90 sm:text-[16.5px]" data-testid="finance-brief-text">
        {sentences.map((s, i) => (
          <span key={i} className={cn(i === 0 && "font-medium text-foreground")}>
            {s}{" "}
          </span>
        ))}
      </p>
      <p className="mt-1 text-[11.5px] leading-4 text-muted-foreground">{meta}</p>
    </div>
  );
};

/** Label/value row for detail sidebars. */
export const Row = ({ label: l, children, mono, testId }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5 text-[13px]" data-testid={testId}>
    <span className="shrink-0 text-muted-foreground">{l}</span>
    <span className={cn("min-w-0 truncate text-right", mono && "tabular")}>{children ?? <span className="text-muted-foreground">–</span>}</span>
  </div>
);

export const DueCell = ({ date, days }) => {
  if (!date) return <span className="text-muted-foreground">–</span>;
  const d = new Date(date);
  const s = d.toLocaleDateString("nb-NO", { day: "2-digit", month: "2-digit", year: "numeric" });
  return (
    <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap tabular">
      {s}
      {days > 0 && <span className="text-[11px] font-medium text-rose-600 dark:text-rose-400">+{days} d</span>}
    </span>
  );
};
